import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';
import { Search, PenTool, X, Wrench, Trash2, Eye, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { generateServiceOrderPdfBlob } from '../../Components/print/printServiceOrder';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

function todayYmd() {
  return new Date().toISOString().split('T')[0];
}

function emptyMaintenanceForm() {
  return {
    service_order_id: '',
    start_date: todayYmd(),
    end_date: todayYmd(),
    start_time: '',
    end_time: '',
    type: 'Preventivo',
    description: '',
    cost: '',
    mileage: '',
    support_files: []
  };
}

function emptyServiceOrderForm(vehicle = null, orderNumber = '') {
  return {
    order_number: orderNumber,
    order_date: todayYmd(),
    workshop_name: '',
    entry_date: todayYmd(),
    entry_time: '',
    maintenance_responsible_name: '',
    reporter_name: '',
    plate: vehicle?.plate || '',
    model: [vehicle?.brand, vehicle?.model].filter(Boolean).join(' '),
    mileage: '',
    vehicle_type: vehicle?.vehicle_type || '',
    maintenance_type: 'Correctivo',
    main_cause: '',
    intervention_description: '',
    observations: '',
    exit_date: '',
    exit_time: '',
    driver_delivery_name: '',
    coordinator_name: '',
    receiver_name: '',
    workshop_responsible_name: '',
    spend_authorizer_name: '',
    total_cost: '',
  };
}

function formatMaintenanceRange(row) {
  const s = row?.start_date || row?.date || '-';
  const e = row?.end_date;
  const t1 = row?.start_time;
  const t2 = row?.end_time;
  const datePart = e && e !== s ? `${s} - ${e}` : `${s}`;
  const timePart = t1 || t2 ? ` (${t1 || '--:--'} - ${t2 || '--:--'})` : '';
  return `${datePart}${timePart}`;
}

function formatServiceOrderMoment(dateValue, timeValue) {
  const datePart = String(dateValue || '').trim();
  const timePart = String(timeValue || '').trim();
  if (!datePart && !timePart) return '-';
  if (datePart && timePart) return `${datePart} ${timePart}`;
  return datePart || timePart;
}

async function openServiceOrderPdf(order, targetWindow = null) {
  const { blob, title } = await generateServiceOrderPdfBlob({
    order,
    company: {
      name: 'SENERFI S.A.S.',
      subtitle: 'Soluciones energeticas forestales integrales S.A.S',
    },
  });
  const url = URL.createObjectURL(blob);
  void recordOperationsDownload({ fileType: 'pdf', moduleKey: 'transportes', reportName: 'Orden de servicio de mantenimiento' });
  const popup = targetWindow && !targetWindow.closed
    ? targetWindow
    : window.open(url, '_blank', 'noopener,noreferrer');

  if (popup && !popup.closed) {
    try {
      popup.location.href = url;
    } catch (_) {}
  } else {
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  setTimeout(() => {
    try { URL.revokeObjectURL(url); } catch (_) {}
  }, 10000);
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
      {children}
    </label>
  );
}

const MILEAGE_COUNTER_WARNING_KM = 500;

function formatMileageSourceForExport(base, override) {
  const overrideMileage = (override?.mileage !== null && override?.mileage !== undefined)
    ? Number(override.mileage)
    : null;
  if (overrideMileage !== null) return 'Ajuste manual';
  const src = String(base?.source || '').toLowerCase();
  if (src === 'pdf') return 'PDF';
  if (src === 'form') return 'Formulario';
  if (src === 'tanqueo') return 'Tanqueo';
  return base?.source ? String(base.source) : '';
}

function semaphoreLabelForExport(status) {
  if (!status?.configured) return 'Sin configurar';
  if (status.remaining === null) return 'Sin kilometraje reportado';
  if (status.remaining <= 0) return 'Vencido';
  if (status.remaining <= MILEAGE_COUNTER_WARNING_KM) return 'Alerta (≤500 km)';
  return 'OK';
}

function counterExportSlice(vehicle, kind, currentMileage) {
  const fieldPrefix = kind === 'oil' ? 'oil_change' : 'brake_change';
  const dueMileage = vehicle[`${fieldPrefix}_due_mileage`];
  const updatedAt = vehicle[`${fieldPrefix}_updated_at`];
  let remaining = null;
  let configured = dueMileage !== null && dueMileage !== undefined;
  if (configured && currentMileage !== undefined && currentMileage !== null) {
    remaining = Number(dueMileage) - Number(currentMileage);
  }
  const status = {
    configured,
    remaining: configured && (currentMileage === undefined || currentMileage === null) ? null : remaining,
  };
  const prefix = kind === 'oil' ? 'ACEITE' : 'FRENOS';
  return {
    [`KM_OBJETIVO_${prefix}`]: configured ? Number(dueMileage) : '',
    [`KM_RESTANTES_${prefix}`]: status.remaining !== null ? status.remaining : '',
    [`SEMAFORO_${prefix}`]: semaphoreLabelForExport(status),
    [`FECHA_CONFIG_${prefix}`]: updatedAt ? String(updatedAt).slice(0, 10) : '',
  };
}

export default function MantenimientosModule() {
  const [vehicles, setVehicles] = useState([]);
  const [mileageMap, setMileageMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [counterModal, setCounterModal] = useState(null); // { kind: 'oil'|'brake', vehicle }
  const [counterInput, setCounterInput] = useState('');
  const [counterSaving, setCounterSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [editingMaintenance, setEditingMaintenance] = useState(null);
  const [returnToHistory, setReturnToHistory] = useState(false);

  const [isServiceOrderModalOpen, setIsServiceOrderModalOpen] = useState(false);
  const [isServiceOrderHistoryOpen, setIsServiceOrderHistoryOpen] = useState(false);
  const [serviceOrderHistoryData, setServiceOrderHistoryData] = useState([]);
  const [editingServiceOrder, setEditingServiceOrder] = useState(null);
  const [returnToServiceOrderHistory, setReturnToServiceOrderHistory] = useState(false);
  const [serviceOrderSaving, setServiceOrderSaving] = useState(false);

  const [formData, setFormData] = useState(emptyMaintenanceForm());
  const [openOrdersForVehicle, setOpenOrdersForVehicle] = useState([]);
  const [loadingOpenOrders, setLoadingOpenOrders] = useState(false);
  const [serviceOrderForm, setServiceOrderForm] = useState(emptyServiceOrderForm());
  const [supportPreviews, setSupportPreviews] = useState([]);
  const [exportFrom, setExportFrom] = useState(todayYmd());
  const [exportTo, setExportTo] = useState(todayYmd());
  const [exporting, setExporting] = useState(false);
  const [exportingPreventive, setExportingPreventive] = useState(false);
  const [mileageDetailMap, setMileageDetailMap] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [res, mileageRes] = await Promise.all([
        api.maintenances.get(),
        api.preoperationals.latestMileage().catch(() => null),
      ]);
      if (res.ok) {
        setVehicles(res.data || []);
      }
      const map = {};
      const detailMap = {};
      (Array.isArray(mileageRes?.data) ? mileageRes.data : []).forEach((row) => {
        const vid = row?.vehicle?.id;
        if (!vid) return;
        detailMap[vid] = {
          base: row?.base || null,
          override: row?.override || null,
          mileage: row?.mileage,
        };
        if (row?.mileage !== null && row?.mileage !== undefined) map[vid] = Number(row.mileage);
      });
      setMileageMap(map);
      setMileageDetailMap(detailMap);
    } catch (error) {
      console.error('Error fetching maintenances:', error);
    } finally {
      setLoading(false);
    }
  };

  const MILEAGE_COUNTERS = {
    oil: { fieldPrefix: 'oil_change', label: 'Cambio de aceite', setApi: api.vehicles.setOilChange },
    brake: { fieldPrefix: 'brake_change', label: 'Cambio de frenos', setApi: api.vehicles.setBrakeChange },
  };
  const getMileageCounterStatus = (vehicle, kind) => {
    const { fieldPrefix } = MILEAGE_COUNTERS[kind];
    const currentMileage = mileageMap[vehicle.id];
    const dueMileage = vehicle[`${fieldPrefix}_due_mileage`];
    if (dueMileage === null || dueMileage === undefined) {
      return { configured: false, remaining: null, dot: 'bg-gray-300', textClass: 'text-gray-400', text: 'Sin configurar' };
    }
    if (currentMileage === undefined || currentMileage === null) {
      return { configured: true, remaining: null, dot: 'bg-gray-300', textClass: 'text-gray-400', text: 'Sin kilometraje reportado' };
    }
    const remaining = Number(dueMileage) - Number(currentMileage);
    if (remaining <= 0) {
      return { configured: true, remaining, dot: 'bg-red-500', textClass: 'text-red-600 font-semibold', text: `Vencido (${Math.abs(remaining)} km)` };
    }
    if (remaining <= MILEAGE_COUNTER_WARNING_KM) {
      return { configured: true, remaining, dot: 'bg-amber-500', textClass: 'text-amber-600 font-semibold', text: `${remaining} km restantes` };
    }
    return { configured: true, remaining, dot: 'bg-green-500', textClass: 'text-green-700', text: `${remaining} km restantes` };
  };

  const openCounterModal = (kind, vehicle) => {
    const status = getMileageCounterStatus(vehicle, kind);
    setCounterModal({ kind, vehicle });
    setCounterInput(status.configured && status.remaining !== null ? String(status.remaining) : '');
  };

  const saveCounter = async () => {
    if (!counterModal) return;
    const { kind, vehicle } = counterModal;
    const { label, setApi } = MILEAGE_COUNTERS[kind];
    const currentMileage = mileageMap[vehicle.id];
    if (currentMileage === undefined || currentMileage === null) {
      alert(`Este vehículo todavía no tiene kilometraje reportado en Preoperacionales/Control de kilometraje — no se puede calcular el ${label.toLowerCase()}.`);
      return;
    }
    const remainingKm = Number(String(counterInput).replace(/\D+/g, ''));
    if (!counterInput || Number.isNaN(remainingKm) || remainingKm < 0) {
      alert(`Ingresa los kilómetros restantes para el próximo ${label.toLowerCase()}.`);
      return;
    }
    setCounterSaving(true);
    try {
      const dueMileage = currentMileage + remainingKm;
      const res = await setApi({ id: vehicle.id, due_mileage: dueMileage, set_at_mileage: currentMileage });
      if (!res?.ok) throw new Error(res?.error || `No fue posible guardar el contador de ${label.toLowerCase()}.`);
      setCounterModal(null);
      setCounterInput('');
      fetchData();
    } catch (e) {
      alert(e?.message || `No fue posible guardar el contador de ${label.toLowerCase()}.`);
    } finally {
      setCounterSaving(false);
    }
  };

  const filteredVehicles = vehicles.filter((v) =>
    v.plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.brand?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const supportLinksOf = useMemo(() => {
    const apiOrigin = process.env.REACT_APP_API_ORIGIN || '';
    const parse = (row) => {
      const out = [];
      const raw = row?.support_files;
      if (raw) {
        try {
          const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
          if (Array.isArray(parsed)) parsed.forEach((p) => { if (p) out.push(String(p)); });
        } catch (_) {}
      }
      if (row?.support_file) out.unshift(String(row.support_file));
      const uniq = Array.from(new Set(out.map((s) => s.trim()).filter(Boolean)));
      return uniq.map((p) => ({
        path: p,
        url: `${apiOrigin}/${p}`.replace(/\/+/g, '/').replace(':/', '://'),
      }));
    };
    return { parse };
  }, []);

  const addSupportFiles = (files) => {
    const arr = Array.from(files || []).filter((f) => f instanceof File);
    if (!arr.length) return;
    const mapped = arr.map((f) => ({ file: f, url: URL.createObjectURL(f) }));
    setFormData((p) => ({ ...p, support_files: [...(p.support_files || []), ...arr] }));
    setSupportPreviews((p) => ([...(p || []), ...mapped]));
  };

  const removeSupportAt = (idx) => {
    setSupportPreviews((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      const item = arr[idx];
      if (item?.url) { try { URL.revokeObjectURL(item.url); } catch (_) {} }
      arr.splice(idx, 1);
      return arr;
    });
    setFormData((p) => {
      const files = Array.isArray(p.support_files) ? [...p.support_files] : [];
      files.splice(idx, 1);
      return { ...p, support_files: files };
    });
  };

  const openNewMaintenance = async (vehicle) => {
    setLoadingOpenOrders(true);
    let orders = [];
    try {
      const res = await api.maintenanceServiceOrders.openByVehicle(vehicle.id);
      if (res.ok) orders = res.data || [];
    } catch (_) {
      // seguir con lista vacía; se avisa abajo
    } finally {
      setLoadingOpenOrders(false);
    }
    if (orders.length === 0) {
      alert('Este vehículo no tiene ninguna Orden de Servicio abierta. Primero crea una Orden de Servicio — el mantenimiento se registra a partir de ella.');
      return;
    }
    setOpenOrdersForVehicle(orders);
    setSelectedVehicle(vehicle);
    setEditingMaintenance(null);
    setFormData({ ...emptyMaintenanceForm(), service_order_id: orders.length === 1 ? String(orders[0].id) : '' });
    setSupportPreviews((prev) => {
      (prev || []).forEach((p) => { try { if (p?.url) URL.revokeObjectURL(p.url); } catch (_) {} });
      return [];
    });
    setIsModalOpen(true);
  };

  const closeMaintenanceModal = () => {
    setIsModalOpen(false);
    setEditingMaintenance(null);
    if (returnToHistory) {
      setIsHistoryOpen(true);
      setReturnToHistory(false);
    }
    setSupportPreviews((prev) => {
      (prev || []).forEach((p) => { try { if (p?.url) URL.revokeObjectURL(p.url); } catch (_) {} });
      return [];
    });
    setFormData((p) => ({ ...p, support_files: [] }));
  };

  const loadHistory = async (vehicleId) => {
    const res = await api.maintenances.history(vehicleId);
    if (res.ok) setHistoryData(res.data || []);
  };

  const openHistory = async (vehicle) => {
    setSelectedVehicle(vehicle);
    setIsHistoryOpen(true);
    setHistoryData([]);
    try {
      await loadHistory(vehicle.id);
    } catch (e) {
      console.error(e);
    }
  };

  const openEditMaintenance = (row) => {
    if (!row || !selectedVehicle) return;
    if (isHistoryOpen) {
      setIsHistoryOpen(false);
      setReturnToHistory(true);
    } else {
      setReturnToHistory(false);
    }
    setEditingMaintenance(row);
    setOpenOrdersForVehicle(row.service_order_id ? [{ id: row.service_order_id, order_number: row.service_order_number }] : []);
    setFormData({
      service_order_id: row.service_order_id ? String(row.service_order_id) : '',
      start_date: row.start_date || row.date || todayYmd(),
      end_date: row.end_date || row.start_date || row.date || todayYmd(),
      start_time: row.start_time || '',
      end_time: row.end_time || '',
      type: row.type || 'Preventivo',
      description: row.description || '',
      cost: row.cost ?? '',
      mileage: row.mileage ?? '',
      support_files: []
    });
    setSupportPreviews((prev) => {
      (prev || []).forEach((p) => { try { if (p?.url) URL.revokeObjectURL(p.url); } catch (_) {} });
      return [];
    });
    setIsModalOpen(true);
  };

  const deleteMaintenance = async (row) => {
    if (!row?.id || !selectedVehicle) return;
    const ok = window.confirm('¿Seguro que deseas borrar este mantenimiento?');
    if (!ok) return;
    try {
      const res = await api.maintenances.delete({ id: row.id });
      if (!res.ok) {
        alert(res.error || 'No fue posible borrar el mantenimiento.');
        return;
      }
      await loadHistory(selectedVehicle.id);
      fetchData();
    } catch (_) {
      alert('No fue posible borrar el mantenimiento.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedVehicle) return;
    if (!editingMaintenance && !formData.service_order_id) {
      alert('Selecciona la Orden de Servicio que autoriza este mantenimiento.');
      return;
    }

    try {
      const startDate = String(formData.start_date || '').trim();
      const endDate = String(formData.end_date || '').trim() || startDate;
      const fd = new FormData();
      fd.append('vehicle_id', String(selectedVehicle.id));
      if (!editingMaintenance) fd.append('service_order_id', String(formData.service_order_id));
      fd.append('start_date', startDate);
      fd.append('end_date', endDate);
      fd.append('start_time', String(formData.start_time || ''));
      fd.append('end_time', String(formData.end_time || ''));
      fd.append('date', startDate);
      fd.append('type', String(formData.type || 'Preventivo'));
      fd.append('description', String(formData.description || ''));
      fd.append('cost', String(formData.cost || ''));
      fd.append('mileage', String(formData.mileage || ''));
      (formData.support_files || []).forEach((f) => {
        if (f instanceof File) fd.append('support_files[]', f);
      });
      let res = null;
      if (editingMaintenance?.id) {
        fd.append('id', String(editingMaintenance.id));
        res = await api.maintenances.update(fd);
      } else {
        res = await api.maintenances.create(fd);
      }
      if (res.ok) {
        closeMaintenanceModal();
        fetchData();
        if ((isHistoryOpen || returnToHistory) && selectedVehicle?.id) {
          await loadHistory(selectedVehicle.id);
        }
      } else {
        alert('Error: ' + res.error);
      }
    } catch (_) {
      alert('Error al guardar');
    }
  };

  const loadServiceOrderHistory = async (vehicle) => {
    const res = await api.maintenanceServiceOrders.history({ vehicle_id: vehicle.id, plate: vehicle.plate });
    if (res.ok) setServiceOrderHistoryData(res.data || []);
  };

  const openNewServiceOrder = async (vehicle) => {
    setSelectedVehicle(vehicle);
    setEditingServiceOrder(null);
    setReturnToServiceOrderHistory(false);
    // Consecutivo global autogenerado por el servidor — el usuario ya no lo
    // escribe a mano (evita duplicados o saltos por error humano). Se
    // vuelve a pedir uno fresco justo antes de guardar, por si alguien más
    // creó una orden mientras este formulario estaba abierto.
    setServiceOrderForm(emptyServiceOrderForm(vehicle, 'Generando...'));
    setIsServiceOrderModalOpen(true);
    try {
      const res = await api.maintenanceServiceOrders.nextNumber();
      if (res?.ok) setServiceOrderForm((p) => ({ ...p, order_number: res.order_number }));
    } catch (_) {
      setServiceOrderForm((p) => ({ ...p, order_number: '' }));
    }
  };

  const closeServiceOrderModal = () => {
    setIsServiceOrderModalOpen(false);
    setEditingServiceOrder(null);
    if (returnToServiceOrderHistory) {
      setIsServiceOrderHistoryOpen(true);
      setReturnToServiceOrderHistory(false);
    }
  };

  const openServiceOrderHistory = async (vehicle) => {
    setSelectedVehicle(vehicle);
    setServiceOrderHistoryData([]);
    setIsServiceOrderHistoryOpen(true);
    try {
      await loadServiceOrderHistory(vehicle);
    } catch (e) {
      console.error(e);
    }
  };

  const openEditServiceOrder = (row) => {
    const vehicle = selectedVehicle && String(selectedVehicle.id) === String(row?.vehicle_id)
      ? selectedVehicle
      : (vehicles.find((v) => String(v.id) === String(row?.vehicle_id)) || {
          id: row?.vehicle_id,
          plate: row?.plate,
          brand: '',
          model: row?.model || '',
          vehicle_type: row?.vehicle_type || '',
        });

    setSelectedVehicle(vehicle);
    if (isServiceOrderHistoryOpen) {
      setIsServiceOrderHistoryOpen(false);
      setReturnToServiceOrderHistory(true);
    } else {
      setReturnToServiceOrderHistory(false);
    }
    setEditingServiceOrder(row);
    setServiceOrderForm({
      order_number: row?.order_number || '',
      order_date: row?.order_date || todayYmd(),
      workshop_name: row?.workshop_name || '',
      entry_date: row?.entry_date || todayYmd(),
      entry_time: row?.entry_time || '',
      maintenance_responsible_name: row?.maintenance_responsible_name || '',
      reporter_name: row?.reporter_name || '',
      plate: row?.plate || vehicle?.plate || '',
      model: row?.model || [vehicle?.brand, vehicle?.model].filter(Boolean).join(' '),
      mileage: row?.mileage ?? '',
      vehicle_type: row?.vehicle_type || vehicle?.vehicle_type || '',
      maintenance_type: row?.maintenance_type || 'Correctivo',
      main_cause: row?.main_cause || '',
      intervention_description: row?.intervention_description || '',
      observations: row?.observations || '',
      exit_date: row?.exit_date || '',
      exit_time: row?.exit_time || '',
      driver_delivery_name: row?.driver_delivery_name || '',
      coordinator_name: row?.coordinator_name || '',
      receiver_name: row?.receiver_name || '',
      workshop_responsible_name: row?.workshop_responsible_name || '',
      spend_authorizer_name: row?.spend_authorizer_name || '',
      total_cost: row?.total_cost ?? '',
    });
    setIsServiceOrderModalOpen(true);
  };

  const handleServiceOrderSubmit = async (e) => {
    e.preventDefault();
    if (!selectedVehicle?.id) return;
    const printWindow = window.open('', '_blank');
    if (printWindow && !printWindow.closed) {
      try {
        printWindow.document.write('<title>Generando PDF...</title><div style="font-family:Arial;padding:24px">Generando PDF de la orden de servicio...</div>');
        printWindow.document.close();
      } catch (_) {}
    }
    try {
      setServiceOrderSaving(true);
      const payload = {
        id: editingServiceOrder?.id || undefined,
        vehicle_id: selectedVehicle.id,
        ...serviceOrderForm,
        plate: serviceOrderForm.plate || selectedVehicle.plate || '',
        model: serviceOrderForm.model || [selectedVehicle?.brand, selectedVehicle?.model].filter(Boolean).join(' '),
        vehicle_type: serviceOrderForm.vehicle_type || selectedVehicle?.vehicle_type || '',
      };
      const res = await api.maintenanceServiceOrders.upsert(payload);
      if (!res?.ok) {
        try { if (printWindow && !printWindow.closed) printWindow.close(); } catch (_) {}
        alert(res?.error || 'No fue posible guardar la orden de servicio.');
        return;
      }
      const savedOrder = {
        ...payload,
        id: res?.id || payload.id || '',
        order_number: res?.order_number || payload.order_number || '',
      };
      closeServiceOrderModal();
      if ((isServiceOrderHistoryOpen || returnToServiceOrderHistory) && selectedVehicle) {
        await loadServiceOrderHistory(selectedVehicle);
      }
      await openServiceOrderPdf(savedOrder, printWindow);
    } catch (e) {
      try { if (printWindow && !printWindow.closed) printWindow.close(); } catch (_) {}
      // request() lanza "HTTP 400: <motivo>" cuando el backend rechaza: se
      // muestra el motivo real en vez de un mensaje genérico.
      const motivo = String(e?.message || '').replace(/^HTTP \d+:\s*/, '').trim();
      alert(motivo ? `No fue posible guardar la orden de servicio: ${motivo}` : 'No fue posible guardar la orden de servicio.');
    } finally {
      setServiceOrderSaving(false);
    }
  };

  const handleExportPreventiveExcel = async () => {
    try {
      setExportingPreventive(true);
      const list = Array.isArray(vehicles) ? vehicles : [];
      if (!list.length) {
        alert('No hay vehículos activos para exportar.');
        return;
      }
      const stamp = new Date().toISOString().slice(0, 10);
      const sheetRows = list.map((v) => {
        const currentMileage = mileageMap[v.id];
        const detail = mileageDetailMap[v.id] || {};
        const kmVigente = (currentMileage !== undefined && currentMileage !== null) ? currentMileage : '';
        const oilSlice = counterExportSlice(v, 'oil', currentMileage);
        const brakeSlice = counterExportSlice(v, 'brake', currentMileage);
        return {
          PLACA: v.plate || '',
          MARCA: v.brand || '',
          MODELO: v.model || '',
          KM_VIGENTE: kmVigente,
          FUENTE_KM: formatMileageSourceForExport(detail.base, detail.override),
          ...oilSlice,
          ...brakeSlice,
        };
      });
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(sheetRows);
      XLSX.utils.book_append_sheet(wb, ws, 'PreventivoVigente');
      void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'transportes', reportName: 'Reporte preventivo vigente' });
      XLSX.writeFile(wb, `reporte_preventivo_${stamp}.xlsx`);
    } catch (error) {
      alert(error?.message || 'No fue posible exportar el reporte preventivo.');
    } finally {
      setExportingPreventive(false);
    }
  };

  const handleExportExcel = async () => {
    const from = String(exportFrom || '').trim();
    const to = String(exportTo || '').trim();
    if (!from || !to) {
      alert('Debes seleccionar fecha inicial y fecha final.');
      return;
    }
    try {
      setExporting(true);
      const [maintRes, ordersRes] = await Promise.all([
        api.maintenances.range({ from, to }),
        api.maintenanceServiceOrders.range({ from, to }),
      ]);

      const maintRows = Array.isArray(maintRes?.data) ? maintRes.data : [];
      const orderRows = Array.isArray(ordersRes?.data) ? ordersRes.data : [];

      const maintSheetRows = maintRows.map((row) => ({
        PLACA: row?.plate || '-',
        VEHICULO: [row?.brand, row?.model].filter(Boolean).join(' ') || '-',
        TIPO_VEHICULO: row?.vehicle_type || '-',
        FECHA_INICIO: row?.start_date || row?.date || '-',
        FECHA_FIN: row?.end_date || '-',
        HORA_INICIO: row?.start_time || '-',
        HORA_FIN: row?.end_time || '-',
        TIPO_MANTENIMIENTO: row?.type || '-',
        DESCRIPCION: row?.description || '-',
        KILOMETRAJE_ODOMETRO: row?.mileage ?? '-',
        COSTO: row?.cost ?? 0,
        REGISTRADO_EL: row?.created_at || '-',
      }));

      const orderSheetRows = orderRows.map((row) => ({
        NUMERO_ORDEN: row?.order_number || '-',
        FECHA_ORDEN: row?.order_date || '-',
        PLACA: row?.plate || '-',
        MODELO: row?.model || '-',
        TIPO_VEHICULO: row?.vehicle_type || '-',
        TALLER: row?.workshop_name || '-',
        FECHA_ENTRADA: row?.entry_date || '-',
        HORA_ENTRADA: row?.entry_time || '-',
        FECHA_SALIDA: row?.exit_date || '-',
        HORA_SALIDA: row?.exit_time || '-',
        RESPONSABLE_MANTENIMIENTO: row?.maintenance_responsible_name || '-',
        REPORTA_HALLAZGO: row?.reporter_name || '-',
        TIPO_MANTENIMIENTO: row?.maintenance_type || '-',
        CAUSA_PRINCIPAL: row?.main_cause || '-',
        INTERVENCION: row?.intervention_description || '-',
        OBSERVACIONES: row?.observations || '-',
        KILOMETRAJE: row?.mileage ?? '-',
        COSTO_TOTAL: row?.total_cost ?? 0,
        CONDUCTOR_ENTREGA: row?.driver_delivery_name || '-',
        COORDINADOR: row?.coordinator_name || '-',
        QUIEN_RECIBE: row?.receiver_name || '-',
        RESPONSABLE_TALLER: row?.workshop_responsible_name || '-',
        ORDENADOR_GASTO: row?.spend_authorizer_name || '-',
        REGISTRADO_EL: row?.created_at || '-',
      }));

      const wb = XLSX.utils.book_new();
      const maintSheet = XLSX.utils.json_to_sheet(maintSheetRows.length ? maintSheetRows : [{ INFO: 'Sin mantenimientos en el rango seleccionado' }]);
      const orderSheet = XLSX.utils.json_to_sheet(orderSheetRows.length ? orderSheetRows : [{ INFO: 'Sin ordenes de servicio en el rango seleccionado' }]);
      XLSX.utils.book_append_sheet(wb, maintSheet, 'Mantenimientos');
      XLSX.utils.book_append_sheet(wb, orderSheet, 'OrdenesServicio');
      void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'transportes', reportName: 'Mantenimientos y órdenes de servicio' });
      XLSX.writeFile(wb, `mantenimientos_${from}_a_${to}.xlsx`);
    } catch (error) {
      alert(error?.message || 'No fue posible exportar la informacion.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-4 space-y-6">
      <div className="bg-white p-4 rounded-xl shadow border border-gray-200 flex flex-col md:flex-row gap-4 justify-between items-center">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Wrench className="w-5 h-5 text-orange-600" />
          Control de Mantenimientos
        </h2>
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar placa..."
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500 text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white p-4 rounded-xl shadow border border-gray-200 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-gray-100 pb-4">
          <div>
            <div className="text-sm font-semibold text-gray-800">Reporte preventivo (estado vigente)</div>
            <div className="text-xs text-gray-500 mt-1">Placa, kilometraje vigente y vencimiento de aceite y frenos. No usa rango de fechas.</div>
          </div>
          <button
            type="button"
            onClick={handleExportPreventiveExcel}
            disabled={exportingPreventive || loading}
            className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-md border border-orange-300 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-800 hover:bg-orange-100 disabled:opacity-70"
          >
            <Download className="w-4 h-4" />
            {exportingPreventive ? 'Exportando...' : 'Exportar preventivo'}
          </button>
        </div>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="text-sm font-semibold text-gray-800">Exportar informacion en Excel</div>
            <div className="text-xs text-gray-500 mt-1">Descarga por rango de fechas las ordenes de servicio y los mantenimientos registrados.</div>
          </div>
          <div className="grid gap-3 md:grid-cols-3 lg:min-w-[640px]">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">Fecha inicio</span>
              <input type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-gray-600">Fecha fin</span>
              <input type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)} className="w-full rounded border border-gray-300 px-3 py-2 text-sm" />
            </label>
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={exporting}
              className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-md text-white px-4 py-2 text-sm font-semibold hover:bg-orange-600 disabled:opacity-70"
              style={{ backgroundColor: '#F07E2D' }}
            >
              <Download className="w-4 h-4" />
              {exporting ? 'Exportando...' : 'Exportar Excel'}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehículo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Último Mantenimiento</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cambio de aceite</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cambio de frenos</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan="6" className="px-6 py-10 text-center">Cargando...</td></tr>
              ) : filteredVehicles.map((v) => {
                const oilStatus = getMileageCounterStatus(v, 'oil');
                const brakeStatus = getMileageCounterStatus(v, 'brake');
                return (
                <tr key={v.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="font-semibold text-gray-900">{v.plate}</span>
                      <span className="text-xs text-gray-500">{v.brand} {v.model}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {v.last_maintenance ? (
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{formatMaintenanceRange(v.last_maintenance)}</span>
                        <span className="text-xs text-gray-500 truncate w-48" title={v.last_maintenance.description}>{v.last_maintenance.description}</span>
                      </div>
                    ) : (
                      <span className="text-xs italic text-gray-400">Sin registros</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm">{v.last_maintenance?.type || '-'}</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex items-center gap-1.5 text-xs ${oilStatus.textClass}`}>
                        <span className={`h-2 w-2 rounded-full ${oilStatus.dot}`} />
                        {oilStatus.text}
                      </span>
                      <button
                        type="button"
                        onClick={() => openCounterModal('oil', v)}
                        className="text-left text-xs font-semibold text-blue-600 hover:text-blue-800"
                      >
                        {oilStatus.configured ? 'Actualizar' : 'Configurar'}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex items-center gap-1.5 text-xs ${brakeStatus.textClass}`}>
                        <span className={`h-2 w-2 rounded-full ${brakeStatus.dot}`} />
                        {brakeStatus.text}
                      </span>
                      <button
                        type="button"
                        onClick={() => openCounterModal('brake', v)}
                        className="text-left text-xs font-semibold text-blue-600 hover:text-blue-800"
                      >
                        {brakeStatus.configured ? 'Actualizar' : 'Configurar'}
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="grid gap-2 md:min-w-[320px]">
                      <div className="grid gap-2 rounded-xl border border-indigo-100 bg-indigo-50/70 px-3 py-2">
                        <button
                          type="button"
                          onClick={() => openNewServiceOrder(v)}
                          className="inline-flex min-h-[36px] items-center justify-center rounded-md border border-indigo-200 bg-white px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                          title="Orden de servicio"
                        >
                          ORDEN DE SERVICIO
                        </button>
                        <button
                          type="button"
                          onClick={() => openServiceOrderHistory(v)}
                          className="inline-flex min-h-[36px] items-center justify-center rounded-md border border-indigo-200 bg-indigo-100 px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-200"
                          title="Historial orden de servicio"
                        >
                          HISTORIAL DE ORDEN DE SERVICIO
                        </button>
                      </div>

                      <div className="grid gap-2 rounded-xl border border-orange-100 bg-orange-50/70 px-3 py-2">
                        <button
                          type="button"
                          onClick={() => openNewMaintenance(v)}
                          className="inline-flex min-h-[36px] items-center justify-center rounded-md border border-orange-200 bg-white px-3 py-2 text-xs font-semibold text-orange-700 hover:bg-orange-100"
                          title="Registrar mantenimiento"
                        >
                          REGISTRAR MANTENIMIENTO
                        </button>
                        <button
                          type="button"
                          onClick={() => openHistory(v)}
                          className="inline-flex min-h-[36px] items-center justify-center rounded-md border border-blue-200 bg-blue-100 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-200"
                          title="Historial de mantenimientos"
                        >
                          HISTORIAL DE MANTENIMIENTOS
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {counterModal && (
        <div className="fixed inset-0 z-[70] bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-sm p-6">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold">{MILEAGE_COUNTERS[counterModal.kind].label} - {counterModal.vehicle.plate}</h3>
              <button type="button" onClick={() => setCounterModal(null)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Kilometraje actual: {mileageMap[counterModal.vehicle.id] !== undefined ? `${mileageMap[counterModal.vehicle.id]} km` : 'sin reportar (Control de kilometraje)'}
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kilómetros restantes para el próximo {MILEAGE_COUNTERS[counterModal.kind].label.toLowerCase()}</label>
            <input
              type="number"
              min="0"
              value={counterInput}
              onChange={(e) => setCounterInput(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder="Ej: 5000"
              autoFocus
            />
            <p className="text-xs text-gray-400 mt-2">Los km restantes se recalculan con el kilometraje vigente del Control de kilometraje (preoperacional, tanqueo o ajuste manual).</p>
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={() => setCounterModal(null)} className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-50 text-sm">Cancelar</button>
              <button
                type="button"
                onClick={saveCounter}
                disabled={counterSaving}
                className="px-4 py-2 rounded text-white text-sm disabled:opacity-60"
                style={{ backgroundColor: '#F07E2D' }}
              >
                {counterSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isServiceOrderModalOpen && (
        <div className="fixed inset-0 z-[70] bg-black bg-opacity-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-5xl p-6 max-h-[92vh] overflow-y-auto my-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-bold">
                  {editingServiceOrder ? 'Editar orden de servicio' : 'Orden de servicio'} - {selectedVehicle?.plate}
                </h3>
                <div className="text-xs text-gray-500 mt-1">Formato FT-TRP-130 | Ingreso y salida de taller</div>
              </div>
              <button type="button" onClick={closeServiceOrderModal}><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={handleServiceOrderSubmit} className="space-y-5">
              <div className="rounded-xl border border-orange-100 bg-orange-50 p-4">
                <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-4">
                  <Field label="Número orden de servicio">
                    <input
                      type="text"
                      required
                      readOnly
                      value={serviceOrderForm.order_number}
                      title="Consecutivo generado automáticamente, no se puede editar"
                      className="w-full rounded border border-orange-200 bg-orange-100 px-3 py-2 text-sm font-semibold text-orange-700 cursor-not-allowed"
                    />
                  </Field>
                  <Field label="Fecha de orden de servicio">
                    <input type="date" required value={serviceOrderForm.order_date} onChange={(e) => setServiceOrderForm((p) => ({ ...p, order_date: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                  </Field>
                  <Field label="Fecha de entrada de vehículo a taller">
                    <input type="date" required value={serviceOrderForm.entry_date} onChange={(e) => setServiceOrderForm((p) => ({ ...p, entry_date: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                  </Field>
                  <Field label="Hora de entrada de vehículo a taller">
                    <input type="time" value={serviceOrderForm.entry_time} onChange={(e) => setServiceOrderForm((p) => ({ ...p, entry_time: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                  </Field>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Field label="Nombre del taller al cual ingresa vehículo">
                  <input type="text" required value={serviceOrderForm.workshop_name} onChange={(e) => setServiceOrderForm((p) => ({ ...p, workshop_name: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                </Field>
                <Field label="Nombre de responsable del mantenimiento">
                  <input type="text" value={serviceOrderForm.maintenance_responsible_name} onChange={(e) => setServiceOrderForm((p) => ({ ...p, maintenance_responsible_name: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                </Field>
                <Field label="Nombre de quien reporta el hallazgo">
                  <input type="text" required value={serviceOrderForm.reporter_name} onChange={(e) => setServiceOrderForm((p) => ({ ...p, reporter_name: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                </Field>
                <Field label="Tipo de mantenimiento">
                  <select value={serviceOrderForm.maintenance_type} onChange={(e) => setServiceOrderForm((p) => ({ ...p, maintenance_type: e.target.value }))} className="w-full border rounded p-2 text-sm">
                    <option value="Correctivo">Correctivo</option>
                    <option value="Preventivo">Preventivo</option>
                    <option value="Otro">Otro</option>
                  </select>
                </Field>
              </div>

              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="mb-3 text-sm font-semibold text-gray-800">Datos del vehículo</div>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <Field label="Placa">
                    <input type="text" readOnly value={serviceOrderForm.plate} className="w-full border rounded p-2 text-sm bg-white" />
                  </Field>
                  <Field label="Modelo">
                    <input type="text" readOnly value={serviceOrderForm.model} className="w-full border rounded p-2 text-sm bg-white" />
                  </Field>
                  <Field label="Kilometraje">
                    <input type="number" min="0" value={serviceOrderForm.mileage} onChange={(e) => setServiceOrderForm((p) => ({ ...p, mileage: e.target.value }))} className="w-full border rounded p-2 text-sm bg-white" />
                  </Field>
                  <Field label="Tipo de vehículo">
                    <input type="text" readOnly value={serviceOrderForm.vehicle_type} className="w-full border rounded p-2 text-sm bg-white" />
                  </Field>
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                <Field label="Descripción de la causa principal que genera el ingreso al taller">
                  <textarea rows="6" required value={serviceOrderForm.main_cause} onChange={(e) => setServiceOrderForm((p) => ({ ...p, main_cause: e.target.value }))} className="w-full border rounded p-2 text-sm" placeholder={'1.\n2.\n3.\n4.\n5.'} />
                </Field>
                <Field label="Descripción de intervención a realizar al vehículo">
                  <textarea rows="6" required value={serviceOrderForm.intervention_description} onChange={(e) => setServiceOrderForm((p) => ({ ...p, intervention_description: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                </Field>
              </div>

              <Field label="Observaciones">
                <textarea rows="4" value={serviceOrderForm.observations} onChange={(e) => setServiceOrderForm((p) => ({ ...p, observations: e.target.value }))} className="w-full border rounded p-2 text-sm" />
              </Field>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Field label="Fecha de salida del vehículo de taller">
                  <input type="date" value={serviceOrderForm.exit_date} onChange={(e) => setServiceOrderForm((p) => ({ ...p, exit_date: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                </Field>
                <Field label="Hora de salida del vehículo de taller">
                  <input type="time" value={serviceOrderForm.exit_time} onChange={(e) => setServiceOrderForm((p) => ({ ...p, exit_time: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                </Field>
                <Field label="Costo total">
                  <input type="number" min="0" step="0.01" value={serviceOrderForm.total_cost} onChange={(e) => setServiceOrderForm((p) => ({ ...p, total_cost: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                </Field>
                <Field label="Ordenador del gasto">
                  <input type="text" value={serviceOrderForm.spend_authorizer_name} onChange={(e) => setServiceOrderForm((p) => ({ ...p, spend_authorizer_name: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                </Field>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="mb-3 text-sm font-semibold text-gray-800">Responsables y firmas</div>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <Field label="Nombre y firma de conductor responsable del ingreso">
                    <input type="text" value={serviceOrderForm.driver_delivery_name} onChange={(e) => setServiceOrderForm((p) => ({ ...p, driver_delivery_name: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                  </Field>
                  <Field label="Coordinador / supervisor mantenimiento y transporte">
                    <input type="text" value={serviceOrderForm.coordinator_name} onChange={(e) => setServiceOrderForm((p) => ({ ...p, coordinator_name: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                  </Field>
                  <Field label="Quien recibe el vehículo en taller">
                    <input type="text" value={serviceOrderForm.receiver_name} onChange={(e) => setServiceOrderForm((p) => ({ ...p, receiver_name: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                  </Field>
                  <Field label="Responsable del mantenimiento en taller">
                    <input type="text" value={serviceOrderForm.workshop_responsible_name} onChange={(e) => setServiceOrderForm((p) => ({ ...p, workshop_responsible_name: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                  </Field>
                </div>
              </div>

              <button type="submit" disabled={serviceOrderSaving} className="w-full text-white py-2 rounded hover:bg-orange-600 disabled:opacity-70" style={{ backgroundColor: '#F07E2D' }}>
                {serviceOrderSaving ? 'Guardando...' : (editingServiceOrder ? 'Actualizar orden de servicio' : 'Guardar orden de servicio')}
              </button>
            </form>
          </div>
        </div>
      )}

      {isServiceOrderHistoryOpen && (
        <div className="fixed inset-0 z-[65] bg-black bg-opacity-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] flex flex-col min-h-0 my-6">
            <div className="p-4 border-b flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold">Historial órdenes de servicio - {selectedVehicle?.plate}</h3>
                <div className="text-xs text-gray-500 mt-1">Consulta y edición por placa</div>
              </div>
              <button type="button" onClick={() => setIsServiceOrderHistoryOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 min-h-0">
              {serviceOrderHistoryData.length === 0 ? (
                <p className="text-gray-500 text-center">No hay órdenes de servicio registradas para esta placa.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 text-left">Consecutivo</th>
                      <th className="p-2 text-left">Estado</th>
                      <th className="p-2 text-left">Fecha orden</th>
                      <th className="p-2 text-left">Taller</th>
                      <th className="p-2 text-left">Tipo</th>
                      <th className="p-2 text-left">Ingreso</th>
                      <th className="p-2 text-left">Salida</th>
                      <th className="p-2 text-left">Reporta hallazgo</th>
                      <th className="p-2 text-right">Costo total</th>
                      <th className="p-2 text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceOrderHistoryData.map((row) => (
                      <tr key={row.id} className="border-b">
                        <td className="p-2 font-semibold text-indigo-700">{row.order_number}</td>
                        <td className="p-2">
                          <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${row.status === 'Completada' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                            {row.status || 'Abierta'}
                          </span>
                        </td>
                        <td className="p-2">{row.order_date || '-'}</td>
                        <td className="p-2">{row.workshop_name || '-'}</td>
                        <td className="p-2">{row.maintenance_type || '-'}</td>
                        <td className="p-2">{formatServiceOrderMoment(row.entry_date, row.entry_time)}</td>
                        <td className="p-2">{formatServiceOrderMoment(row.exit_date, row.exit_time)}</td>
                        <td className="p-2">{row.reporter_name || '-'}</td>
                        <td className="p-2 text-right">{row.total_cost || 0}</td>
                        <td className="p-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-xs font-medium text-sky-700 hover:bg-sky-100"
                              title="Ver PDF"
                              onClick={() => openServiceOrderPdf(row)}
                            >
                              <Eye className="w-3.5 h-3.5" />
                              PDF
                            </button>
                            <button
                              type="button"
                              className="p-1 text-orange-600 hover:bg-orange-50 rounded"
                              title="Editar orden de servicio"
                              onClick={() => openEditServiceOrder(row)}
                            >
                              <PenTool className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-md p-6 max-h-[90vh] overflow-y-auto my-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">{editingMaintenance ? 'Editar Mantenimiento' : 'Registrar Mantenimiento'} - {selectedVehicle?.plate}</h3>
              <button type="button" onClick={closeMaintenanceModal}><X className="w-5 h-5" /></button>
            </div>
            {editingMaintenance?.created_at && (
              <div className="text-xs text-gray-600 mb-3">
                Reportado el: {String(editingMaintenance.created_at).replace('T', ' ').slice(0, 19)}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Orden de servicio que autoriza este mantenimiento</label>
                {editingMaintenance ? (
                  <div className="w-full rounded border border-gray-200 bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700">
                    {openOrdersForVehicle.find((o) => String(o.id) === String(formData.service_order_id))?.order_number || '—'}
                  </div>
                ) : openOrdersForVehicle.length <= 1 ? (
                  <div className="w-full rounded border border-orange-200 bg-orange-100 px-3 py-2 text-sm font-semibold text-orange-700">
                    {openOrdersForVehicle[0]?.order_number || '—'}
                  </div>
                ) : (
                  <select
                    className="w-full border rounded p-2"
                    required
                    value={formData.service_order_id}
                    onChange={(e) => setFormData({ ...formData, service_order_id: e.target.value })}
                  >
                    <option value="">Selecciona la orden...</option>
                    {openOrdersForVehicle.map((o) => (
                      <option key={o.id} value={o.id}>{o.order_number} — {o.workshop_name || 'sin taller'} ({o.order_date})</option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Fecha</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Fecha inicio</label>
                    <input
                      type="date"
                      className="w-full border rounded p-2"
                      required
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Fecha fin</label>
                    <input
                      type="date"
                      className="w-full border rounded p-2"
                      required
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Hora inicio</label>
                    <input
                      type="time"
                      className="w-full border rounded p-2"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Hora fin</label>
                    <input
                      type="time"
                      className="w-full border rounded p-2"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tipo</label>
                <select className="w-full border rounded p-2" value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                  <option value="Preventivo">Preventivo</option>
                  <option value="Correctivo">Correctivo</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descripción</label>
                <textarea className="w-full border rounded p-2" rows="3" required value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Costo</label>
                  <input type="number" className="w-full border rounded p-2" value={formData.cost} onChange={(e) => setFormData({ ...formData, cost: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Kilometraje/Odómetro</label>
                  <input type="number" className="w-full border rounded p-2" value={formData.mileage} onChange={(e) => setFormData({ ...formData, mileage: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Agregar Soporte (Opcional)</label>
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                  onChange={(e) => { addSupportFiles(e.target.files); try { e.target.value = ''; } catch (_) {} }}
                />
                {supportPreviews.length > 0 && (
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {supportPreviews.map((p, idx) => (
                      <div key={`${idx}-${p.url}`} className="relative border rounded overflow-hidden">
                        <img src={p.url} alt={`Soporte ${idx + 1}`} className="w-full h-20 object-cover" />
                        <button
                          type="button"
                          onClick={() => removeSupportAt(idx)}
                          className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                          title="Eliminar"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {editingMaintenance && (
                  <div className="mt-3">
                    {(() => {
                      const links = supportLinksOf.parse(editingMaintenance);
                      if (!links.length) return null;
                      return (
                        <div className="text-xs text-gray-700">
                          <div className="font-medium mb-1">Soportes actuales:</div>
                          <div className="flex flex-wrap gap-2">
                            {links.map((l, i) => (
                              <a key={`${l.path}-${i}`} href={l.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                Ver {i + 1}
                              </a>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
              <button type="submit" className="w-full text-white py-2 rounded hover:bg-orange-600" style={{ backgroundColor: '#F07E2D' }}>Guardar</button>
            </form>
          </div>
        </div>
      )}

      {isHistoryOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col min-h-0 my-6">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">Historial de Mantenimientos - {selectedVehicle?.plate}</h3>
              <button type="button" onClick={() => setIsHistoryOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 min-h-0">
              {historyData.length === 0 ? (
                <p className="text-gray-500 text-center">No hay registros.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="p-2 text-left">Nº Orden</th>
                      <th className="p-2 text-left">Fecha</th>
                      <th className="p-2 text-left">Tipo</th>
                      <th className="p-2 text-left">Descripción</th>
                      <th className="p-2 text-right">Kilometraje/Odómetro</th>
                      <th className="p-2 text-center">Soporte</th>
                      <th className="p-2 text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.map((h) => (
                      <tr key={h.id} className="border-b">
                        <td className="p-2 font-semibold text-orange-700">{h.service_order_number || '—'}</td>
                        <td className="p-2">{formatMaintenanceRange(h)}</td>
                        <td className="p-2">{h.type}</td>
                        <td className="p-2">{h.description}</td>
                        <td className="p-2 text-right">{h.mileage}</td>
                        <td className="p-2 text-center">
                          {(() => {
                            const links = supportLinksOf.parse(h);
                            if (!links.length) return null;
                            if (links.length === 1) {
                              return (
                                <a href={links[0].url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">
                                  Ver
                                </a>
                              );
                            }
                            return (
                              <div className="flex flex-col items-center gap-1">
                                <span className="text-xs text-gray-600">{links.length} archivos</span>
                                <div className="flex flex-wrap justify-center gap-1">
                                  {links.slice(0, 4).map((l, i) => (
                                    <a key={`${l.path}-${i}`} href={l.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">
                                      Ver {i + 1}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="p-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              type="button"
                              className="p-1 text-orange-600 hover:bg-orange-50 rounded"
                              title="Editar"
                              onClick={() => openEditMaintenance(h)}
                            >
                              <PenTool className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                              title="Borrar"
                              onClick={() => deleteMaintenance(h)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
